import type { Location, StoreWithDistance } from '@context/StoresContext';
import { isClosedStoreHourValue, weeklyHours } from '@utils/store';
import { getDistanceFromShopperToStore } from './getDistanceFromShopperToStore';

const BULLET = '•';

type ShopperGeo = Pick<Location, 'latitude' | 'longitude'>;

function formatDistanceKm(distanceInMeters: number | undefined) {
  if (distanceInMeters == null || Number.isNaN(distanceInMeters)) {
    return null;
  }
  const km = distanceInMeters / 1000;
  const rounded = km < 10 ? Math.round(km * 10) / 10 : Math.round(km);
  const displayKm = km > 0 && rounded === 0 ? 0.1 : rounded;
  return { km: displayKm };
}

/** Compact “10am-9pm” style from today’s row when open/close are times. */
function formatTodayHoursCompact(store: StoreWithDistance) {
  const ordered = weeklyHours(
    store.data.store_hours,
    store.data.store_hour_exceptions
  );
  const today = ordered[0];
  if (!today?.open || !today?.close) return null;
  const open = today.open.trim();
  const close = today.close.trim();
  if (isClosedStoreHourValue(open) || isClosedStoreHourValue(close)) {
    return null;
  }
  return `${open}-${close}`;
}

export type StorePreviewDetailsParts = {
  line: string;
};

/**
 * Builds the secondary line: address • distance (from user location when shared) • hours.
 */
export function formatStorePreviewDetails(
  store: StoreWithDistance,
  t: (key: string, opts?: Record<string, string | number>) => string,
  shopperGeolocation?: ShopperGeo | null
): StorePreviewDetailsParts {
  const address = store.data.address_line_1?.trim() ?? '';
  const dist = formatDistanceKm(
    getDistanceFromShopperToStore(store, shopperGeolocation)
  );
  const distanceStr = dist
    ? t('product.findInStore.distanceAway', { count: dist.km })
    : null;
  const hours = formatTodayHoursCompact(store);

  const segments = [address, distanceStr, hours].filter(Boolean) as string[];
  return {
    line: segments.join(` ${BULLET} `),
  };
}
